import React, { useState } from 'react'
import { Container, Row, Col, Card, Button, Alert, Form, Table, Spinner, Tab, Nav, Badge } from 'react-bootstrap'
import { faPlus, faEdit, faTrash, faDownload, faCheck, faSpinner, faTimes, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AppBadge from 'shared/badges/app-badge'
import RiskBadge from 'shared/badges/risk-badge'
import SeverityBadge from 'shared/badges/severity-badge'
import StatusBadge from 'shared/badges/status-badge'
import ProcessStatusBadge from 'shared/badges/process-status-badge'
import { ActionButton, IconButton } from 'shared/buttons'
import CenteredSpinner from 'shared/centered-spinner'
import TableLoading from 'shared/tables/table-loading'
import TableError from 'shared/tables/table-error'
import CardLoading from 'shared/card-loading'
import CardError from 'shared/card-error'
import Search from 'shared/forms/search'
import SelectField from 'shared/forms/SelectField'
import CheckboxField from 'shared/forms/CheckboxField'
import NumberField from 'shared/forms/NumberField'

import RoleScoreBadge from 'shared/badges/role-score-badge'
import ConflictBadge from 'shared/badges/conflict-badge'
import InternalConflict from 'shared/badges/internal-conflict-badge'
import HighRiskPermissionBadge from 'shared/badges/high-risk-permission-badge'
import SuperUserPermissionBadge from 'shared/badges/superuser-permission-badge'
import IssueBadge from 'shared/badges/issue-badge'
import WorkflowActionBadge from 'shared/badges/workflow-action-badge'
import NewReviewBadge from 'shared/badges/new-review-badge'
import AssignedBadge from 'shared/badges/assigned-badge'
import UserStatusBadge from 'shared/badges/user-status-badge'
import LockedBadge from 'shared/badges/locked-badge'

import TerminatedToggle from 'duties/filters/terminated-toggle'
import InactiveUsersToggle from 'duties/filters/inactive-users'
import InactiveRolesToggle from 'duties/filters/inactive-roles'
import MissingPayroll from 'duties/filters/missing-payroll'
import NoDataAccess from 'duties/filters/no-data-access'
import SystemUsersToggle from 'duties/filters/system-users-toggle'

const Section = ({ title, description, children }) => (
  <section className="mb-5">
    <h3 className="mb-2">{title}</h3>
    {description && <p className="text-muted mb-3">{description}</p>}
    <Card body className="bg-light">{children}</Card>
  </section>
)

const Swatch = ({ name, hex, textClass = 'text-white' }) => (
  <Col xs={6} sm={4} md={3} lg={2} className="mb-3">
    <div
      className={`rounded p-3 ${textClass}`}
      style={{ backgroundColor: hex, minHeight: 80 }}
    >
      <div className="fw-bold">{name}</div>
      <small>{hex}</small>
    </div>
  </Col>
)

const Usage = ({ children }) => (
  <pre className="bg-white border rounded p-2 mt-3 mb-0" style={{ fontSize: '0.8rem' }}>
    <code>{children}</code>
  </pre>
)

// ─── Analytics tabs ───────────────────────────────────────────────────────────

const ColorsTab = () => (
  <>
    <Section title="Theme Colors" description="Defined in frontend/src/custom.scss via $theme-colors and $colors.">
      <Row>
        <Swatch name="primary" hex="#1E2E48" />
        <Swatch name="danger" hex="#E77152" />
        <Swatch name="success" hex="#27AB83" />
        <Swatch name="warning" hex="#E9C46A" textClass="text-dark" />
        <Swatch name="info" hex="#606060" />
        <Swatch name="secondary" hex="#D6E4EA" textClass="text-dark" />
        <Swatch name="light" hex="#efefef" textClass="text-dark" />
        <Swatch name="link" hex="#5c80bb" />
      </Row>
    </Section>
    <Section title="Accent Colors" description="Used for badges, charts, and inline accents.">
      <Row>
        <Swatch name="blue" hex="#1E2E48" />
        <Swatch name="cyan" hex="#38BEC9" />
        <Swatch name="green" hex="#27AB83" />
        <Swatch name="yellow" hex="#E9C46A" textClass="text-dark" />
        <Swatch name="orange" hex="#fd7e14" />
        <Swatch name="red" hex="#E77152" />
        <Swatch name="teal" hex="#20c997" />
      </Row>
    </Section>
  </>
)

const TypographyTab = () => (
  <Section title="Typography" description="Font family: sofia-pro. Use Bootstrap heading classes and text utilities.">
    <h1>Heading 1 — page title</h1>
    <h2>Heading 2 — section title</h2>
    <h3>Heading 3 — subsection</h3>
    <h4>Heading 4</h4>
    <h5>Heading 5</h5>
    <h6>Heading 6</h6>
    <p>Body copy. Default size <code>1rem</code>.</p>
    <p className="text-muted">Muted text — use for secondary info, captions, helper text.</p>
    <p className="text-primary">Primary-colored text — use sparingly for emphasis.</p>
    <p className="small">Small text — use for footnotes and dense table cells.</p>
  </Section>
)

const ButtonsTab = () => (
  <>
    <Section title="React Bootstrap Buttons" description="Use stock Bootstrap variants. Avoid variant='secondary' on buttons — pair light with text-primary for neutral actions.">
      <div className="d-flex flex-wrap gap-2">
        <Button variant="primary">Primary</Button>
        <Button variant="danger">Danger</Button>
        <Button variant="success">Success</Button>
        <Button variant="warning">Warning</Button>
        <Button variant="info">Info</Button>
        <Button variant="light" className="text-primary">Light + text-primary</Button>
        <Button variant="outline-primary">Outline primary</Button>
        <Button variant="link">Link</Button>
      </div>
      <Usage>{`<Button variant="primary">Save</Button>
<Button variant="light" className="text-primary">Cancel</Button>`}</Usage>
    </Section>

    <Section title="Button Sizes">
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <Button variant="primary" size="sm">Small</Button>
        <Button variant="primary">Default</Button>
        <Button variant="primary" size="lg">Large</Button>
      </div>
    </Section>

    <Section title="ActionButton" description="shared/buttons/action-button.jsx — Button with optional FontAwesome icon and loading state.">
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <ActionButton icon={faPlus} label="Create" variant="primary" />
        <ActionButton icon={faDownload} label="Export" variant="light" className="text-primary" />
        <ActionButton icon={faTrash} label="Delete" variant="danger" />
        <ActionButton icon={faPlus} label="Saving" loading loadingLabel="Saving..." />
      </div>
      <Usage>{`import { ActionButton } from 'shared/buttons'
<ActionButton icon={faPlus} label="Create" variant="primary" onClick={...} />`}</Usage>
    </Section>

    <Section title="IconButton" description="shared/buttons/icon-button.jsx — square icon-only button with hover-to-danger styling, used in row actions.">
      <div className="d-flex flex-wrap gap-2">
        <IconButton icon={faEdit} title="Edit" />
        <IconButton icon={faTrash} title="Delete" />
      </div>
      <Usage>{`import { IconButton } from 'shared/buttons'
<IconButton icon={faEdit} onClick={...} title="Edit" />`}</Usage>
    </Section>
  </>
)

const AnalyticsBadgesTab = () => (
  <>
    <Section title="AppBadge" description="shared/badges/app-badge.jsx — base badge with custom variant classes. Prefer this over raw <Badge>.">
      <div className="d-flex flex-wrap gap-2">
        <AppBadge variant="primary">primary</AppBadge>
        <AppBadge variant="secondary">secondary</AppBadge>
        <AppBadge variant="success">success</AppBadge>
        <AppBadge variant="danger">danger</AppBadge>
        <AppBadge variant="warning">warning</AppBadge>
        <AppBadge variant="info">info</AppBadge>
        <AppBadge variant="high">high</AppBadge>
        <AppBadge variant="ai">ai</AppBadge>
      </div>
      <Usage>{`import AppBadge from 'shared/badges/app-badge'
<AppBadge variant="success">Approved</AppBadge>`}</Usage>
    </Section>

    <Section title="Domain Badges" description="Pre-styled badges that wrap AppBadge with domain semantics.">
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <div>
          <div className="small text-muted mb-1">RiskBadge</div>
          <div className="d-flex gap-1">
            <RiskBadge count={0} />
            <RiskBadge count={1} />
            <RiskBadge count={3} />
          </div>
        </div>
        <div>
          <div className="small text-muted mb-1">SeverityBadge</div>
          <div className="d-flex gap-1">
            <SeverityBadge severity="low" />
            <SeverityBadge severity="medium" />
            <SeverityBadge severity="high" />
            <SeverityBadge severity="critical" />
          </div>
        </div>
        <div>
          <div className="small text-muted mb-1">StatusBadge</div>
          <StatusBadge title="Active" tooltipText="Currently active" />
        </div>
        <div>
          <div className="small text-muted mb-1">UserStatusBadge</div>
          <div className="d-flex gap-1">
            <UserStatusBadge state="active" />
            <UserStatusBadge state="blocked" />
            <UserStatusBadge state="deleted" />
          </div>
        </div>
        <div>
          <div className="small text-muted mb-1">ProcessStatusBadge</div>
          <div className="d-flex gap-1">
            <ProcessStatusBadge
              status="processing"
              statusConfig={{
                processing: { icon: faSpinner, text: 'Processing', color: 'text-warning', spin: true },
                completed: { icon: faCheck, text: 'Done', color: 'text-success' },
                failed: { icon: faTimes, text: 'Failed', color: 'text-danger' }
              }}
            />
            <ProcessStatusBadge
              status="completed"
              statusConfig={{
                completed: { icon: faCheck, text: 'Done', color: 'text-success' }
              }}
            />
          </div>
        </div>
      </div>
    </Section>

    <Section title="Alerts" description="React Bootstrap <Alert> for inline messages. Avoid variant='secondary'.">
      <Alert variant="primary" className="mb-2">Primary alert</Alert>
      <Alert variant="success" className="mb-2">Success alert</Alert>
      <Alert variant="warning" className="mb-2">Warning alert</Alert>
      <Alert variant="danger" className="mb-0">Danger alert</Alert>
    </Section>
  </>
)

const FormsTab = () => {
  const [params, setParams] = useState({ search: '', status: '', count: '', flagged: false })
  return (
    <>
      <Section title="Search" description="shared/forms/search.tsx — debounced search input, synced to params.search.">
        <Search params={params} setParams={setParams} placeholder="Search..." />
        <Usage>{`import Search from 'shared/forms/search'
<Search params={params} setParams={setParams} placeholder="Search vendors" />`}</Usage>
      </Section>

      <Section title="SelectField" description="shared/forms/SelectField.jsx — labeled select with clear button, follows the filter-row pattern.">
        <SelectField
          params={params}
          setParams={setParams}
          accessorKey="status"
          title="Status"
          placeholder="All statuses"
          options={[
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' }
          ]}
        />
      </Section>

      <Section title="NumberField & CheckboxField" description="Standard labeled inputs for filter panels.">
        <NumberField
          params={params}
          setParams={setParams}
          accessorKey="count"
          title="Count"
          placeholder="0"
        />
        <CheckboxField
          params={params}
          setParams={setParams}
          accessorKey="flagged"
          label="Flagged only"
          helperText="Show only flagged records"
        />
      </Section>

      <Section title="Raw form controls" description="Use these for ad-hoc forms outside the shared filter pattern.">
        <Form>
          <Form.Group className="mb-2">
            <Form.Label>Text input</Form.Label>
            <Form.Control type="text" placeholder="Enter value" />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Select</Form.Label>
            <Form.Select>
              <option>One</option>
              <option>Two</option>
            </Form.Select>
          </Form.Group>
          <Form.Check label="Checkbox" />
        </Form>
      </Section>
    </>
  )
}

const TablesTab = () => (
  <>
    <Section title="Standard Table" description="Bootstrap <Table> with hover. Wrap in .table-responsive for sticky header inside the analytics app.">
      <Table hover responsive>
        <thead>
          <tr>
            <th>Vendor</th>
            <th>Amount</th>
            <th>Risk</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Acme Corp</td>
            <td>$1,234.56</td>
            <td><RiskBadge count={3} /></td>
            <td><AppBadge variant="success">Approved</AppBadge></td>
          </tr>
          <tr>
            <td>Widget Co</td>
            <td>$89.00</td>
            <td><RiskBadge count={1} /></td>
            <td><AppBadge variant="warning">Pending</AppBadge></td>
          </tr>
        </tbody>
      </Table>
    </Section>

    <Section title="TableLoading & TableError" description="Drop into a <tbody> while loading or on error. Pass column count to span the row.">
      <Table>
        <thead>
          <tr><th>Col A</th><th>Col B</th><th>Col C</th></tr>
        </thead>
        <tbody>
          <TableLoading columns={3} />
        </tbody>
      </Table>
      <Table>
        <thead>
          <tr><th>Col A</th><th>Col B</th><th>Col C</th></tr>
        </thead>
        <tbody>
          <TableError columns={3} />
        </tbody>
      </Table>
      <Usage>{`<tbody>
  {isLoading && <TableLoading columns={6} />}
  {isError && <TableError columns={6} />}
  {data?.map(...)}
</tbody>`}</Usage>
    </Section>
  </>
)

const AnalyticsLayoutTab = () => (
  <>
    <Section title="CenteredSpinner" description="shared/centered-spinner.jsx — full-block loading indicator.">
      <CenteredSpinner />
      <Usage>{`import CenteredSpinner from 'shared/centered-spinner'
if (isLoading) return <CenteredSpinner />`}</Usage>
    </Section>

    <Section title="Card" description="React Bootstrap <Card>. Used throughout the dashboard for KPI tiles and content panels.">
      <Row>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title>KPI Tile</Card.Title>
              <h2 className="text-primary">$12,345</h2>
              <Card.Text className="text-muted small">Last 30 days</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>Header</Card.Header>
            <Card.Body>Content panel.</Card.Body>
          </Card>
        </Col>
      </Row>
    </Section>

    <Section title="CardLoading & CardError" description="shared/card-loading.jsx / shared/card-error.jsx — the card-body equivalents of TableLoading/TableError. Used by dashboard and report chart, table and KPI cards while their data is in flight or after it fails.">
      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>Spend by vendor</Card.Header>
            <Card.Body>
              <CardLoading minHeight={200} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>Spend by vendor</Card.Header>
            <Card.Body>
              <CardError
                title="Couldn't load this chart"
                onRetry={() => {}}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Usage>{`import CardLoading from 'shared/card-loading'
import CardError from 'shared/card-error'

if (isError) return <CardError title="Couldn't load this chart" onRetry={refetch} />
if (isLoading) return <CardLoading />`}</Usage>
    </Section>

    <Section title="Inline Spinner" description="Use <Spinner size='sm'> for inline async feedback (next to a button label, in a status row).">
      <div className="d-flex gap-2 align-items-center">
        <Spinner animation="border" size="sm" /> Loading…
        <Spinner animation="grow" variant="warning" />
      </div>
    </Section>
  </>
)

const ANALYTICS_TABS = [
  { eventKey: 'colors', title: 'Colors', component: <ColorsTab /> },
  { eventKey: 'typography', title: 'Typography', component: <TypographyTab /> },
  { eventKey: 'buttons', title: 'Buttons', component: <ButtonsTab /> },
  { eventKey: 'badges', title: 'Badges & Alerts', component: <AnalyticsBadgesTab /> },
  { eventKey: 'forms', title: 'Forms & Filters', component: <FormsTab /> },
  { eventKey: 'tables', title: 'Tables', component: <TablesTab /> },
  { eventKey: 'layout', title: 'Layout', component: <AnalyticsLayoutTab /> }
]

// ─── Access Management tabs ───────────────────────────────────────────────────

const SodBadgesTab = () => (
  <>
    <Section title="RoleScoreBadge" description="shared/badges/role-score-badge.jsx — colors a role's score into Low / Medium / High / Critical bands. Pass showCount to render the raw number instead of the label.">
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <RoleScoreBadge count={5} />
        <RoleScoreBadge count={25} />
        <RoleScoreBadge count={45} />
        <RoleScoreBadge count={70} />
        <RoleScoreBadge count={70} showCount />
      </div>
      <Usage>{`import RoleScoreBadge from 'shared/badges/role-score-badge'
<RoleScoreBadge count={role.score} />
<RoleScoreBadge count={role.score} showCount />`}</Usage>
    </Section>

    <Section title="ConflictBadge / InternalConflict" description="Conflict counts on a role or user. ConflictBadge shows external conflicts; InternalConflict is for conflicts contained within a single role.">
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <ConflictBadge count={3} />
        <ConflictBadge text="Conflicting" />
        <InternalConflict count={2} />
        <InternalConflict textDisplay="Internal conflicts" />
      </div>
      <Usage>{`import ConflictBadge from 'shared/badges/conflict-badge'
import InternalConflict from 'shared/badges/internal-conflict-badge'
<ConflictBadge count={user.conflict_count} />
<InternalConflict count={role.internal_conflict_count} />`}</Usage>
    </Section>

    <Section title="HighRiskPermissionBadge / SuperUserPermissionBadge" description="Flag permissions that are high risk or grant superuser access. Pass displayCount=false to render the label instead of the number.">
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <HighRiskPermissionBadge count={4} />
        <HighRiskPermissionBadge count={4} displayCount={false} />
        <SuperUserPermissionBadge count={1} />
        <SuperUserPermissionBadge count={1} displayCount={false} />
      </div>
    </Section>

    <Section title="IssueBadge" description="Status pill for the Issues queue. Accepts 'No Issues' | 'Pending' | 'Unresolved'.">
      <div className="d-flex flex-wrap gap-2">
        <IssueBadge status="No Issues" />
        <IssueBadge status="Pending" />
        <IssueBadge status="Unresolved" count={3} />
      </div>
    </Section>

    <Section title="WorkflowActionBadge" description="Single-letter action code (A/R/F/C/H) used in audit logs and workflow history.">
      <div className="d-flex flex-wrap gap-2">
        <WorkflowActionBadge action="A" />
        <WorkflowActionBadge action="R" />
        <WorkflowActionBadge action="F" />
        <WorkflowActionBadge action="C" />
        <WorkflowActionBadge action="H" />
      </div>
    </Section>

    <Section title="NewReviewBadge / AssignedBadge" description="NewReviewBadge surfaces pending review counts. AssignedBadge renders the assignee (role or employee) on a business rule.">
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <NewReviewBadge count={5} />
        <NewReviewBadge count={5} showCount={false} />
        <AssignedBadge rule={{ assignable: { name: 'AP Manager' }, assignable_type: 'Role' }} />
        <AssignedBadge rule={{ assignable: { name: 'Jane Doe' }, assignable_type: 'Employee' }} />
        <AssignedBadge rule={{ assignable: null }} />
      </div>
    </Section>

    <Section title="LockedBadge" description="shared/badges/locked-badge.jsx — marks a report its owner has locked against edits. Forwards its ref, so it can open a popover on click.">
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <LockedBadge />
      </div>
    </Section>
  </>
)

const SodFiltersTab = () => {
  const [params, setParams] = useState({ terminated: false, active: true, showSystem: false, missingPayroll: false, noDataAccess: false })
  return (
    <>
      <Section title="Filter row layout" description="The SOD index pages use duties/filters/index.jsx — a flex row of pills (Search, DepartmentDropdown, RoleDropdown, ConflictDropdown, Category) followed by toggles. Use this as the canonical layout for any new SOD list page.">
        <Usage>{`import Filters from 'duties/filters'
<Filters filterParams={params} setFilterParams={setParams} dataType="Role" />
// dataType: 'Role' | 'Employee' | 'Permission'`}</Usage>
      </Section>

      <Section title="Toggle filters" description="Pure switches that flip a boolean on params. Live previews below — flip them to see the params object update.">
        <Row className="g-3">
          <Col sm={6} md={4}><TerminatedToggle params={params} setParams={setParams} /></Col>
          <Col sm={6} md={4}><InactiveUsersToggle params={params} setParams={setParams} /></Col>
          <Col sm={6} md={4}><InactiveRolesToggle params={params} setParams={setParams} /></Col>
          <Col sm={6} md={4}><SystemUsersToggle params={params} setParams={setParams} /></Col>
          <Col sm={6} md={4}><MissingPayroll params={params} setParams={setParams} /></Col>
          <Col sm={6} md={4}><NoDataAccess params={params} setParams={setParams} /></Col>
        </Row>
        <Usage>{`current params = ${JSON.stringify(params)}`}</Usage>
      </Section>

      <Section title="Dropdown filters" description="API-backed selects rendered via react-select. They share a (filterParams, setFilterParams) prop shape with the toggle filters so they compose into the same row.">
        <ul className="mb-0">
          <li><code>duties/filters/role-dropdown.jsx</code> — useRoles, writes <code>role_id</code></li>
          <li><code>duties/filters/conflict-dropdown.jsx</code> — useConflictOptions, writes <code>conflict_id</code></li>
          <li><code>shared/department-dropdown.tsx</code> — used by SOD list pages, writes <code>department</code></li>
        </ul>
      </Section>
    </>
  )
}

const SodStatsTab = () => {
  const stats = [
    { name: 'Roles Creating Conflicts', value: 18, diff: 14, subtext: 'of All Roles' },
    { name: 'Roles with Internal Conflicts', value: 7, diff: -3, subtext: 'of All Roles' },
    { name: 'Users with High Risk Permissions', value: 42, diff: 9, subtext: 'of Active Users' },
    { name: 'Users with Super User Permissions', value: 6, diff: 0, subtext: 'of Active Users' }
  ]
  return (
    <Section title="Stat cards" description="duties/stats/index.jsx — KPI tile pattern used on the SOD overview. Each card pairs a count with a percent-of-population pill.">
      <Row className="g-3">
        {stats.map((s) => (
          <Col sm={6} lg={3} key={s.name}>
            <Card>
              <Card.Body>
                <Row>
                  <Col sm="auto"><h4>{s.value}</h4></Col>
                  <Col className="text-end">
                    <Badge pill className="text-center fw-normal text-secondary badge-secondary">
                      {s.diff !== 0 && (
                        <FontAwesomeIcon icon={s.diff > 0 ? faArrowUp : faArrowDown} className="me-1" />
                      )}
                      {Math.abs(s.diff)}% {s.subtext}
                    </Badge>
                  </Col>
                </Row>
                <div className="text-muted">{s.name}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <Usage>{`import Stats from 'duties/stats'
<Stats /> // pulls from useAccessManagementCounts hook`}</Usage>
    </Section>
  )
}

const SodExportTab = () => (
  <Section title="SodExport" description="duties/shared/Export.jsx — opens a modal to queue a server-side export of the current filter params. Wire one onto every SOD index page so users can download what they're looking at.">
    <button className="btn btn-light btn-sm">
      <span className="text-primary">
        <FontAwesomeIcon icon={faDownload} className="me-1" /> Export
      </span>
    </button>
    <Usage>{`import SodExport from 'duties/shared/Export'
<SodExport
  params={filterParams}
  defaultName="Roles"
  source="roles"
  size="sm"
/>`}</Usage>
  </Section>
)

const SodLayoutTab = () => (
  <>
    <Section title="Page wrapper" description="layout/access-management/page.jsx is the SOD page shell — applies the access-management sidebar, navbar, and content padding. Every SOD route renders inside one.">
      <Usage>{`import Page from 'layout/access-management/page'
<Page title="Roles">
  <Filters ... />
  <Stats />
  <RolesTable />
</Page>`}</Usage>
    </Section>

    <Section title="PageTitle & breadcrumbs" description="layout/access-management/page-title.jsx renders the breadcrumb trail at the top of detail pages.">
      <Usage>{`import PageTitle from 'layout/access-management/page-title'
<PageTitle
  title={role.name}
  breadcrumbs={[{ name: 'Roles', url: 'roles' }]}
/>`}</Usage>
    </Section>

    <Section title="Tabs" description="duties/shared/tabs/ — the underlined-active-tab pattern used on Role, Employee, and Permission detail pages. Use the .tabs-active class on the active anchor.">
      <ul className="nav">
        <li className="nav-item"><a className="nav-link tabs-active" href="#">Overview</a></li>
        <li className="nav-item"><a className="nav-link text-muted" href="#">Permissions</a></li>
        <li className="nav-item"><a className="nav-link text-muted" href="#">Conflicts</a></li>
        <li className="nav-item"><a className="nav-link text-muted" href="#">Audit Log</a></li>
      </ul>
    </Section>

    <Section title="Empty / loading page" description="layout/access-management/empty-page.jsx — full-height spinner inside the SOD shell. Use while the initial route data loads.">
      <Usage>{`import EmptyPage from 'layout/access-management/empty-page'
if (isLoading) return <EmptyPage />`}</Usage>
    </Section>
  </>
)

const SOD_TABS = [
  { eventKey: 'sod-badges', title: 'Domain Badges', component: <SodBadgesTab /> },
  { eventKey: 'sod-filters', title: 'Filters & Toggles', component: <SodFiltersTab /> },
  { eventKey: 'sod-stats', title: 'Stat Cards', component: <SodStatsTab /> },
  { eventKey: 'sod-export', title: 'Export', component: <SodExportTab /> },
  { eventKey: 'sod-layout', title: 'Layout & Tabs', component: <SodLayoutTab /> }
]

// ─── Top-level tabbed shell ───────────────────────────────────────────────────

const AnalyticsStyleGuide = () => (
  <Tab.Container defaultActiveKey="colors">
    <Row>
      <Col md={2}>
        <Nav variant="pills" className="flex-column sticky-top" style={{ top: '1rem' }}>
          {ANALYTICS_TABS.map(({ eventKey, title }) => (
            <Nav.Item key={eventKey}>
              <Nav.Link eventKey={eventKey}>{title}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </Col>
      <Col md={10}>
        <Tab.Content>
          {ANALYTICS_TABS.map(({ eventKey, component }) => (
            <Tab.Pane key={eventKey} eventKey={eventKey}>
              {component}
            </Tab.Pane>
          ))}
        </Tab.Content>
      </Col>
    </Row>
  </Tab.Container>
)

const AccessManagementStyleGuide = () => (
  <Tab.Container defaultActiveKey="sod-badges">
    <Row>
      <Col md={2}>
        <Nav variant="pills" className="flex-column sticky-top" style={{ top: '1rem' }}>
          {SOD_TABS.map(({ eventKey, title }) => (
            <Nav.Item key={eventKey}>
              <Nav.Link eventKey={eventKey}>{title}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </Col>
      <Col md={10}>
        <Tab.Content>
          {SOD_TABS.map(({ eventKey, component }) => (
            <Tab.Pane key={eventKey} eventKey={eventKey}>
              {component}
            </Tab.Pane>
          ))}
        </Tab.Content>
      </Col>
    </Row>
  </Tab.Container>
)

const TOP_TABS = [
  { eventKey: 'analytics', title: 'Analytics Style Guide', component: <AnalyticsStyleGuide /> },
  { eventKey: 'access-management', title: 'Access Management Style Guide', component: <AccessManagementStyleGuide /> }
]

const StyleGuide = () => (
  <Container fluid>
    <div className="mb-4">
      <h1 className="mb-1">Style Guide</h1>
      <p className="text-muted mb-0">
        Shared components and patterns used across the app. Source lives in{' '}
        <code>frontend/src/shared/</code>,{' '}
        <code>frontend/src/duties/</code>, and{' '}
        <code>frontend/src/layout/access-management/</code>.
      </p>
    </div>

    <Tab.Container defaultActiveKey="analytics">
      <Nav variant="tabs" className="mb-4">
        {TOP_TABS.map(({ eventKey, title }) => (
          <Nav.Item key={eventKey}>
            <Nav.Link eventKey={eventKey}>{title}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <Tab.Content>
        {TOP_TABS.map(({ eventKey, component }) => (
          <Tab.Pane key={eventKey} eventKey={eventKey}>
            {component}
          </Tab.Pane>
        ))}
      </Tab.Content>
    </Tab.Container>
  </Container>
)

export default StyleGuide
